import React, { useState, useContext, useMemo, useCallback, useEffect } from 'react';
import { Box, Text, ResponsiveContext } from 'grommet';
import useSWR, { useSWRConfig } from 'swr';
import Modal from '../../../Modal';
import Loading from '../../../Loading';
import { NotificationsContext } from '../../../NotificationsProvider';
import { handleErrors, silentFetcher } from '../../../../utils/errors';
import { formatCurrency } from '../../../../utils/numbers';
import FastPath from './FastPath';
import FindInvoicesStep from './FindInvoicesStep';
import AllocateStep from './AllocateStep';

const ReconciliationWizard = ({
    journalEntryType = 'payment',
    onClose,
    onSubmit,
    makeCallToLinkInvoice = true,
    transactionId,
    recipientId,
    amountToMatch,
    ctaText,
    singleInvoiceOnly = false,
}) => {
    const { mutate: globalMutate } = useSWRConfig();
    const notificationCtx = useContext(NotificationsContext);
    const size = useContext(ResponsiveContext);
    const isMobile = size === 'small';

    const [step, setStep] = useState(null); // null = loading, 'fast-path' | 1 | 2
    const [foundInvoices, setFoundInvoices] = useState([]);
    const [unfoundItems, setUnfoundItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const formatRecordType = (record) => {
        if (!record) return '';
        return record.replace('external-', '').split('-').join(' ');
    };

    const journalType = formatRecordType(journalEntryType);

    // Fetch transaction data
    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(notificationCtx.showNotification);

    const { data: payment, mutate: mutatePayment } = useSWR(
        transactionId ? `/api/transactions/${transactionId}` : null,
        fetcher,
        { revalidateOnFocus: false }
    );

    // Derive from props or fetched payment data
    const effectiveRecipientId = recipientId || payment?.recipient_id;
    const effectiveAmountToMatch = amountToMatch ?? payment?.amount;

    const { data: customer } = useSWR(
        effectiveRecipientId ? `/api/customers/${effectiveRecipientId}` : null,
        fetcher,
        { revalidateOnFocus: false }
    );

    // Fetch all open invoices for this customer (for fuzzy matching)
    const { data: openInvoicesData, isLoading: isLoadingInvoices } = useSWR(
        effectiveRecipientId ? `/api/invoices/select?status=all&customer_id=${effectiveRecipientId}` : null,
        silentFetcher,
        { revalidateOnFocus: false }
    );

    const allOpenInvoices = useMemo(
        () => openInvoicesData?.invoices || [],
        [openInvoicesData]
    );

    // Fetch suggested invoices by ID
    const suggestionMatchIds = useMemo(() =>
        payment?.payment_match_suggestions?.matches?.map(Number) || [],
    [payment]
    );

    const { data: matchedInvoicesData, isLoading: isLoadingMatches } = useSWR(
        suggestionMatchIds.length > 0
            ? `/api/invoices?ids_to_include=${encodeURIComponent(JSON.stringify(suggestionMatchIds))}`
            : null,
        silentFetcher,
        { revalidateOnFocus: false }
    );

    const matchedInvoices = matchedInvoicesData?.invoices || [];

    // Determine the preview source (only use actual file URLs, not match_source identifiers)
    const previewSource = useMemo(() => {
        if (!payment) return null;
        const source = payment.payment_match_suggestions?.match_source;
        if (source && (source.startsWith('http') || source.startsWith('/'))) return source;
        return payment.txn_files?.[0]?.url || null;
    }, [payment]);

    // Determine initial step once data is loaded
    useEffect(() => {
        if (step !== null) return; // Already decided

        // Wait for payment data if we have a transactionId
        if (transactionId && !payment) return;

        // Wait for matched invoices to finish loading
        if (isLoadingMatches) return;

        const hasMatches = suggestionMatchIds.length > 0;

        if (hasMatches && matchedInvoices.length > 0) {
            // Check if all matches were found and amounts balance
            const allFound = suggestionMatchIds.length === matchedInvoices.length;

            if (allFound) {
                // Pre-fill applied amounts
                const invoicesWithApplied = matchedInvoices.map(inv => ({
                    ...inv,
                    applied: parseFloat(inv.open_balance),
                }));

                const totalApplied = invoicesWithApplied.reduce(
                    (sum, inv) => sum + (inv.applied || 0), 0
                );

                if (totalApplied === effectiveAmountToMatch) {
                    // Perfect match → fast path
                    setFoundInvoices(invoicesWithApplied);
                    setStep('fast-path');
                    return;
                }
            }

            // Partial match → step 1 with pre-selected invoices
            setFoundInvoices(matchedInvoices.map(inv => ({
                ...inv,
                applied: parseFloat(inv.open_balance),
            })));
            setStep(1);
        } else {
            // No suggestions → step 1
            setStep(1);
        }
    }, [payment, matchedInvoices, isLoadingMatches, suggestionMatchIds, transactionId, effectiveAmountToMatch, step]);

    // Link invoice to transaction (PUT request)
    const linkInvoice = useCallback((invoices) => {
        if (!transactionId || !invoices || invoices.length === 0) return;
        if (isSubmitting) return;
        setIsSubmitting(true);

        fetch(`/api/transactions/${transactionId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: transactionId,
                apply_payment: invoices.map(invoice => ({
                    invoice_id: invoice.id,
                    amount: invoice.applied || parseFloat(invoice.amount),
                })),
            }),
        })
            .then(handleErrors)
            .then((data) => {
                globalMutate('/api/company/unknown-payments');
                setIsSubmitting(false);
                if (onSubmit) onSubmit(data);
            })
            .catch(err => {
                notificationCtx.showNotification(err);
                setIsSubmitting(false);
            });
    }, [transactionId, isSubmitting, globalMutate, onSubmit, notificationCtx]);

    // Handle CTA for non-link mode (RecordJournalEntry)
    const onCTAclick = useCallback((invoices) => {
        const data = invoices.map(invoice => ({
            invoice_id: invoice.id,
            amount: invoice.applied || parseFloat(invoice.amount),
        }));
        onSubmit(data);
    }, [onSubmit]);

    const handleConfirm = makeCallToLinkInvoice ? linkInvoice : onCTAclick;

    // Step navigation handlers
    const handleFindNext = (invoices, unfound) => {
        setFoundInvoices(invoices);
        setUnfoundItems(unfound);
        setStep(2);
    };

    const handleAllocateBack = () => {
        setStep(1);
    };

    const handleFastPathEdit = () => {
        setStep(1);
    };

    // Re-fetch transaction after a file upload so previewSource updates
    const handleUploaded = useCallback(() => {
        mutatePayment();
    }, [mutatePayment]);

    // For single invoice mode, skip step 2
    const handleSingleInvoiceConfirm = (invoices, unfound) => {
        handleConfirm(invoices);
    };

    // Title
    const title = effectiveAmountToMatch ? `Reconcile ${formatCurrency(effectiveAmountToMatch)}` : 'Reconcile';

    const isLoading = step === null || (transactionId && !payment);

    const content = isLoading ? (
        <Loading />
    ) : (
        <Box fill>
            <Box flex pad={{ horizontal: 'small', vertical: 'small' }}>
                {step === 'fast-path' && (
                    <FastPath
                        invoices={foundInvoices}
                        amountToMatch={effectiveAmountToMatch}
                        previewSource={previewSource}
                        onConfirm={handleConfirm}
                        onEdit={handleFastPathEdit}
                        isSubmitting={isSubmitting}
                    />
                )}

                {step === 1 && (
                    <FindInvoicesStep
                        customerId={effectiveRecipientId}
                        customerGroupId={customer?.customer_group_id}
                        transactionId={transactionId}
                        amountToMatch={effectiveAmountToMatch}
                        previewSource={previewSource}
                        initialFoundInvoices={foundInvoices}
                        allOpenInvoices={allOpenInvoices}
                        isLoadingInvoices={isLoadingInvoices}
                        onNext={singleInvoiceOnly ? handleSingleInvoiceConfirm : handleFindNext}
                        onUploaded={handleUploaded}
                    />
                )}

                {step === 2 && (
                    <AllocateStep
                        invoices={foundInvoices}
                        unfoundItems={unfoundItems}
                        amountToMatch={effectiveAmountToMatch}
                        journalType={journalType}
                        onBack={handleAllocateBack}
                        onConfirm={handleConfirm}
                        isSubmitting={isSubmitting}
                    />
                )}
            </Box>
        </Box>
    );

    return (
        <Modal
            title={title}
            onClose={onClose}
            contentProps={{
                direction: 'column',
                pad: { horizontal: 'small' },
                fill: true,
            }}
            sectionProps={{
                hideBorder: true,
                fill: true,
                flex: true,
                headerProps: { pad: { bottom: '0', horizontal: 'xsmall', top: 'xsmall' } },
            }}
            containerProps={{
                overflow: 'hidden',
                height: '100vh',
                width: '100vw',
                round: 'none',
            }}
        >
            {content}
        </Modal>
    );
};



export default ReconciliationWizard;
